import { supabase } from '../lib/supabase';

/**
 * STREAK SERVICE
 * Handles all streak calculation and activity tracking logic
 */

export const streakService = {
  /**
   * Record a memorization activity
   * Automatically updates daily activity and streak
   */
  async recordActivity(userId, activityType, metadata = {}) {
    try {
      const { data, error } = await supabase?.rpc('record_memorization_activity', {
        p_user_id: userId,
        p_activity_type: activityType,
        p_metadata: metadata
      });

      if (error) throw error;
      return { success: true, error: null };
    } catch (error) {
      console.error('Error recording activity:', error);
      return { success: false, error: error?.message };
    }
  },

  /**
   * Get comprehensive streak information for a user
   */
  async getStreakInfo(userId) {
    try {
      const { data, error } = await supabase?.rpc('get_user_streak_info', {
        p_user_id: userId
      });

      if (error) throw error;

      // Transform to camelCase
      const streakInfo = data?.[0] ? {
        currentStreak: data?.[0]?.current_streak || 0,
        longestStreak: data?.[0]?.longest_streak || 0,
        lastActivityDate: data?.[0]?.last_activity_date,
        streakStartedDate: data?.[0]?.streak_started_date,
        isActive: data?.[0]?.is_active || false,
        daysSinceActivity: data?.[0]?.days_since_activity || 0,
        todaysActivityCount: data?.[0]?.todays_activity_count || 0
      } : {
        currentStreak: 0,
        longestStreak: 0,
        lastActivityDate: null,
        streakStartedDate: null,
        isActive: false,
        daysSinceActivity: 0,
        todaysActivityCount: 0
      };

      return { data: streakInfo, error: null };
    } catch (error) {
      console.error('Error fetching streak info:', error);
      return { data: null, error: error?.message };
    }
  },

  /**
   * Check and update streak status (for daily cron jobs)
   */
  async checkStreakStatus(userId) {
    try {
      const { data, error } = await supabase?.rpc('check_and_update_streak_status', {
        p_user_id: userId
      });

      if (error) throw error;
      return { success: true, error: null };
    } catch (error) {
      console.error('Error checking streak status:', error);
      return { success: false, error: error?.message };
    }
  },

  /**
   * Get daily activity for a date range
   */
  async getDailyActivity(userId, startDate, endDate = null) {
    try {
      let query = supabase
        ?.from('user_daily_activity')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.gte('activity_date', startDate)
        ?.order('activity_date', { ascending: false });

      if (endDate) {
        query = query?.lte('activity_date', endDate);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Transform to camelCase
      const activities = data?.map(activity => ({
        id: activity?.id,
        userId: activity?.user_id,
        activityDate: activity?.activity_date,
        memorizationSessions: activity?.memorization_sessions || 0,
        versesPracticed: activity?.verses_practiced || 0,
        versesAddedToLibrary: activity?.verses_added_to_library || 0,
        timeSpentMinutes: activity?.time_spent_minutes || 0,
        reviewsCompleted: activity?.reviews_completed || 0,
        hasActivity: activity?.has_activity || false,
        qualifiesForStreak: activity?.qualifies_for_streak || false,
        createdAt: activity?.created_at,
        updatedAt: activity?.updated_at
      })) || [];

      return { data: activities, error: null };
    } catch (error) {
      console.error('Error fetching daily activity:', error);
      return { data: [], error: error?.message };
    }
  },

  /**
   * Get streak history for visualization
   */
  async getStreakHistory(userId, days = 30) {
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate?.setDate(endDate?.getDate() - days);

      const { data: activities, error } = await this.getDailyActivity(
        userId,
        startDate?.toISOString()?.split('T')?.[0],
        endDate?.toISOString()?.split('T')?.[0]
      );

      if (error) throw error;

      // Create a map of dates with activity
      const activityMap = {};
      activities?.forEach(activity => {
        activityMap[activity?.activityDate] = activity;
      });

      // Generate complete date range
      const history = [];
      const currentDate = new Date(startDate);
      
      while (currentDate <= endDate) {
        const dateStr = currentDate?.toISOString()?.split('T')?.[0];
        const activity = activityMap?.[dateStr];
        
        history?.push({
          date: dateStr,
          hasActivity: activity?.hasActivity || false,
          qualifiesForStreak: activity?.qualifiesForStreak || false,
          memorizationSessions: activity?.memorizationSessions || 0,
          versesPracticed: activity?.versesPracticed || 0
        });
        
        currentDate?.setDate(currentDate?.getDate() + 1);
      }

      return { data: history, error: null };
    } catch (error) {
      console.error('Error fetching streak history:', error);
      return { data: [], error: error?.message };
    }
  },

  /**
   * Calculate streak percentage (how many days in last 30 had activity)
   */
  async getStreakPercentage(userId, days = 30) {
    try {
      const { data: history, error } = await this.getStreakHistory(userId, days);
      
      if (error) throw error;

      const activeDays = history?.filter(day => day?.hasActivity)?.length || 0;
      const percentage = Math.round((activeDays / days) * 100);

      return { data: percentage, error: null };
    } catch (error) {
      console.error('Error calculating streak percentage:', error);
      return { data: 0, error: error?.message };
    }
  }
};

/**
 * Activity Types for Streak Tracking
 */
export const ACTIVITY_TYPES = {
  PRACTICE_SESSION: 'practice_session',
  REVIEW_SESSION: 'review_session',
  VERSE_ADDED: 'verse_added',
  REVIEW_COMPLETED: 'review_completed',
  PHASE_ADVANCED: 'phase_advanced',
  QUIZ_COMPLETED: 'quiz_completed'
};

/**
 * Helper: Track verse practice activity
 */
export const trackVersePractice = async (userId, verseCount = 1) => {
  return await streakService?.recordActivity(
    userId,
    ACTIVITY_TYPES?.PRACTICE_SESSION,
    { verses_count: verseCount }
  );
};

/**
 * Helper: Track verse review activity
 */
export const trackVerseReview = async (userId, verseCount = 1) => {
  return await streakService?.recordActivity(
    userId,
    ACTIVITY_TYPES?.REVIEW_SESSION,
    { verses_count: verseCount }
  );
};

/**
 * Helper: Track verse addition activity
 */
export const trackVerseAdded = async (userId) => {
  return await streakService?.recordActivity(
    userId,
    ACTIVITY_TYPES?.VERSE_ADDED,
    { verses_count: 1 }
  );
};

/**
 * Helper: Track review completion
 */
export const trackReviewCompleted = async (userId, verseCount = 1) => {
  return await streakService?.recordActivity(
    userId,
    ACTIVITY_TYPES?.REVIEW_COMPLETED,
    { verses_count: verseCount }
  );
};
function getStreakData(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: getStreakData is not implemented yet.', args);
  return null;
}

export { getStreakData };